import React from 'react';

import Heading from '@atlaskit/heading';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import EmailIcon from '@atlaskit/icon/glyph/email';
import HipchatDialOutIcon from '@atlaskit/icon/glyph/hipchat/dial-out';
import LocationIcon from '@atlaskit/icon/glyph/location';
import LockIcon from '@atlaskit/icon/glyph/lock';
import PersonCircleIcon from '@atlaskit/icon/glyph/person-circle';
import UnlockIcon from '@atlaskit/icon/glyph/unlock';
import { Box, xcss } from '@atlaskit/primitives';
import {
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants';
import { useIntl } from '@atlassian/jira-intl';
import { type Benefit, BenefitsList } from '../../../../common/ui/benefits-list';

import messages from './messages';

type BenefitsSlideProps = {
	projectType: ProjectType;
};

export const BenefitsSlide = ({ projectType }: BenefitsSlideProps) => {
	const { formatMessage } = useIntl();

	const benefitsMap: Map<string, Benefit[]> = new Map(
		Object.entries({
			[SOFTWARE_PROJECT]: [
				{
					icon: <LockIcon label="Lock Icon" />,
					title: formatMessage(messages.userRolesAndPermissions),
					description: formatMessage(messages.haveMoreControl),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <PersonCircleIcon label="Person Circle Icon" />,
					title: formatMessage(messages.anonymousAccess),
					description: formatMessage(messages.giveSecureAccess),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <CommentIcon label="Comment Icon" />,
					title: formatMessage(messages.businessHourSupport),
					description: formatMessage(messages.getSupport),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
			],
			[SERVICE_DESK_PROJECT]: [
				{
					icon: <HipchatDialOutIcon label="Dial Out Icon" />,
					title: formatMessage(messages.onCallManagement),
					description: formatMessage(messages.createCustomRules),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <EmailIcon label="Email Icon" />,
					title: formatMessage(messages.unlimitedEmailNotifications),
					description: formatMessage(messages.updateEveryone),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <CommentIcon label="Comment Icon" />,
					title: formatMessage(messages.businessHourSupport),
					description: formatMessage(messages.getSupport),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
			],
			[PRODUCT_DISCOVERY_PROJECT]: [
				{
					icon: <UnlockIcon label="Unlock Icon" />,
					title: formatMessage(messages.customProjectRoles),
					description: formatMessage(messages.getMoreGranularity),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <LocationIcon label="Location Pin Icon" />,
					title: formatMessage(messages.dataResidency),
					description: formatMessage(messages.chooseDataHosting),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
				{
					icon: <CommentIcon label="Comment Icon" />,
					title: formatMessage(messages.businessHourSupport),
					description: formatMessage(messages.getSupport),
					iconBackgroundStyles: xcss({ backgroundColor: 'color.background.input' }),
				},
			],
		}),
	);

	const benefits = benefitsMap.get(projectType);

	return (
		<Box xcss={ContainerStyles}>
			<Heading size="small">{formatMessage(messages.standardPlanSlideHeader)}</Heading>
			<Box xcss={BenefitsBodyStyles}>
				<BenefitsList benefits={benefits || []} />
			</Box>
		</Box>
	);
};

const ContainerStyles = xcss({
	width: '400px',
	paddingTop: '0',
	paddingBottom: '0',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	boxSizing: 'border-box',
});

const BenefitsBodyStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	marginTop: 'space.300',
	gap: 'space.300',
	height: '230px',
});
